import { useCallback } from "react";

import { CompleteM365MailboxConnectInput } from "~/gql/graphqlBase.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import {
	useCompleteM365MailboxConnectMutation,
	useDisconnectM365MailboxMutation
} from "~/services/endpoints/apiEndpoints";

const CONNECT_ACTION = "connecting your Microsoft mailbox";
const DISCONNECT_ACTION = "disconnecting the mailbox";
const EXPIRED_STATE_MESSAGE = "This connection attempt expired — please try connecting again.";

export interface CompleteMailboxConnectResult {
	errorMessage: string | null;
	mailboxAddress: string | null;
}

export interface UseM365MailboxMutationsReturn {
	completeMailboxConnect: (input: CompleteM365MailboxConnectInput) => Promise<CompleteMailboxConnectResult>;
	disconnectMailbox: () => Promise<boolean>;
	isCompleting: boolean;
	isDisconnecting: boolean;
}

const useM365MailboxMutations = (): UseM365MailboxMutationsReturn => {
	const { run, runForResult } = useMutationRunner();
	const [completeMutation, { isLoading: isCompleting }] = useCompleteM365MailboxConnectMutation();
	const [disconnectMutation, { isLoading: isDisconnecting }] = useDisconnectM365MailboxMutation();

	const completeMailboxConnect = useCallback(
		async (input: CompleteM365MailboxConnectInput): Promise<CompleteMailboxConnectResult> => {
			const genericError = snackMessages.actionError(CONNECT_ACTION);
			const { data, error } = await runForResult(
				completeMutation({ input }),
				(result) => result.completeM365MailboxConnect,
				(errors) =>
					errors?.some((item) => /expired|invalid/i.test(item.message)) ? EXPIRED_STATE_MESSAGE : genericError,
				{ silent: true }
			);
			const payload = data?.completeM365MailboxConnect;
			if (error) {
				return {
					errorMessage: payload?.errors?.some((item) => /expired|invalid/i.test(item.message))
						? EXPIRED_STATE_MESSAGE
						: genericError,
					mailboxAddress: null
				};
			}
			return { errorMessage: null, mailboxAddress: payload?.status?.mailboxAddress ?? null };
		},
		[completeMutation, runForResult]
	);

	const disconnectMailbox = useCallback(
		(): Promise<boolean> =>
			run(disconnectMutation(), (data) => data.disconnectM365Mailbox, snackMessages.actionError(DISCONNECT_ACTION), {
				successMessage: "Mailbox disconnected."
			}),
		[disconnectMutation, run]
	);

	return { completeMailboxConnect, disconnectMailbox, isCompleting, isDisconnecting };
};

export default useM365MailboxMutations;
